import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
  useDeferredValue,
  useTransition,
} from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import PopupMenu from "../../components/PopupMenu/PopupMenu";
import "./Shop.css";
import soon from "../../assets/img/soon.png";
import instance from "../../api/axios.js";

const norm = (s) => s.toLowerCase().trim();

const Nl = ({ text }) => {
  if (!text) return null;
  const parts = text.split(/\/n|\\n|\n/);
  return parts.map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < parts.length - 1 && <br />}
    </React.Fragment>
  ));
};

const SHEET_NAMES_EN = {
  "Вино": "Wine",
};

const translateSheet = (name) => SHEET_NAMES_EN[name] || name;

const CATEGORY_ORDER_GROUPS = [
  "Кофе",
  "Кальяны",
  "Коктейли",
  "Моктейли",
  "Вино",
  "Крепкий алкоголь",
  "Пиво",
  "Сидр",
  "Софт дринкс",
  "Милкшейки",
  "Фирменные лимонады",
  "Чай",
  "Тарелочки",
  "Сладкое",
  "Сладкое, but no sugar",
];

const getCategoryGroup = (categoryName) => {
  const name = (categoryName || "").trim();

  if (name === "Кофе") return "Кофе";
  if (name === "Кальяны") return "Кальяны";
  if (name === "Коктейли" || name === "Коктели") return "Коктейли";
  if (name === "Моктейли") return "Моктейли";
  if (name.startsWith("Вино") || name.startsWith("Wine")) return "Вино";
  if (name === "Крепкий алкоголь") return "Крепкий алкоголь";
  if (name === "Пиво") return "Пиво";
  if (name === "Сидр") return "Сидр";
  if (name === "Софт дринкс") return "Софт дринкс";
  if (name === "Милкшейки") return "Милкшейки";
  if (name === "Фирменные лимонады") return "Фирменные лимонады";
  if (name.startsWith("Чай")) return "Чай";
  if (name === "Тарелочки") return "Тарелочки";
  if (name === "Сладкое") return "Сладкое";
  if (name === "Сладкое, but no sugar") return "Сладкое, but no sugar";

  return null;
};

const sortCategories = (categoryNames) => {
  const firstSeenIndex = new Map();

  categoryNames.forEach((name, index) => {
    if (!firstSeenIndex.has(name)) {
      firstSeenIndex.set(name, index);
    }
  });

  return [...categoryNames].sort((a, b) => {
    const groupA = getCategoryGroup(a);
    const groupB = getCategoryGroup(b);
    const orderA = groupA ? CATEGORY_ORDER_GROUPS.indexOf(groupA) : Number.MAX_SAFE_INTEGER;
    const orderB = groupB ? CATEGORY_ORDER_GROUPS.indexOf(groupB) : Number.MAX_SAFE_INTEGER;

    if (orderA !== orderB) return orderA - orderB;

    return (firstSeenIndex.get(a) ?? 0) - (firstSeenIndex.get(b) ?? 0);
  });
};

const ProductRow = React.memo(({ p, onSelect }) => {
  const hasPhoto = !!p.photo;

  const weight = p.volume || (() => {
    const m = p.shortDesc?.match(/(\d+)\s*(мл|л|г|гр)(?:\s|\/|$)/i);
    return m ? `${m[1]} ${m[2]}` : "";
  })();

  return (
    <div
      className={`shop__row${hasPhoto ? "" : " shop__row--plain"}`}
      onClick={hasPhoto ? () => onSelect(p) : undefined}
    >
      {hasPhoto && (
        <img
          src={p.photo}
          alt={p.name}
          className="shop__thumb"
          onError={onImgError}
          decoding="async"
        />
      )}
      <div className="shop__info">
        <div className="shop__itemName">
          <span>{p.name}</span>
          {weight && <span className="shop__itemWeight">&nbsp;{weight}</span>}
        </div>
        {hasPhoto && p.desc && <div className="shop__itemDesc"><Nl text={p.desc} /></div>}
      </div>
      {p.price != null && (
        <div className="shop__price">
          {p.price}<span className="shop__priceSign">&thinsp;&#8381;</span>
        </div>
      )}
    </div>
  );
});

const onImgError = (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = soon;
};

const normalizeRows = (menuData) => {
  let lastCat = "";
  let autoId = 1;
  const items = [];

  if (menuData && typeof menuData === "object") {
    for (const sheetName in menuData) {
      if (Array.isArray(menuData[sheetName])) {
        for (const r of menuData[sheetName]) {
          const get = (key) => {
            const v = r && r[key];
            return (typeof v === "string" ? v : v ?? "").toString();
          };

          const rawCat = get("Категория").trim();
          const subCategory = get("Подкатегория").trim();
          
          const sheetNameEn = translateSheet(sheetName);
          let category;
          if (subCategory) {
            category = `${sheetNameEn}: ${subCategory}`;
          } else {
            category = rawCat || lastCat || "Разное";
            if (rawCat) lastCat = rawCat;
          }

          const name = get("Название").trim();
          const extrasRaw = get("Дополнительно").replace(/\r/g, "").trim();
          const extras = extrasRaw
            ? extrasRaw
                .split(";")
                .map((s) => s.trim())
                .filter(Boolean)
            : [];
          const finalName = name || extras[0] || "";

          const priceNum = get("Цена")
            .replace(/[^\d.,]/g, "")
            .replace(",", ".");
          const price = priceNum ? Number(priceNum) : null;

          const rehashPrice = get("Перезабивка");
          const rehashPriceNum = rehashPrice
            .replace(/[^\d.,]/g, "")
            .replace(",", ".");
          const rehash = rehashPriceNum ? Number(rehashPriceNum) : null;

          const photoSmall = get("Сжатое фото").trim();
          const photoRaw = photoSmall || get("Фото").trim();
          const photo = photoRaw || null;

          const desc = get("Описание").replace(/\r/g, "").trim();
          const status = get("Статус").trim();
          const composition = get("Состав").trim();
          const volume = get("Объём").trim();
          const country = get("Страна").trim();
          const extrasLabel = get("Название дополнительно").trim();

          if (!finalName && !price && !photo && !desc) continue;

          items.push({
            _id: r._id || `json-${get("№") || autoId++}`,
            category: { name: category },
            subCategory: subCategory ? `${sheetNameEn}: ${subCategory}` : null,
            name: finalName,
            photo,
            price,
            rehash,
            shortDesc: price ? `${price} ₽` : "",
            desc,
            extras,
            extrasLabel,
            status,
            composition,
            volume,
            country,
          });
        }
      }
    }
  }

  return items;
};

const Shop = () => {
  const products = useSelector((s) => s.products.list);

  const [selectedCat, setSelectedCat] = useState("all");
  const [search, setSearch] = useState("");
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const deferredSearch = useDeferredValue(search);

  const inputRef = useRef(null);
  const searchBoxRef = useRef(null);

  const catRef = useRef(null);
  const [catOpen, setCatOpen] = useState(false);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        setLoading(true);
        const response = await instance.get("/api/menu");
        if (response.data.ok && response.data.menu) {
          setMenuData(response.data.menu);
        } else {
          setError("Не удалось загрузить меню");
        }
      } catch (err) {
        console.error("Ошибка загрузки меню:", err);
        setError("Ошибка загрузки меню");
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, []);

  const jsonProducts = useMemo(
    () => normalizeRows(menuData || {}),
    [menuData]
  );

  const dataProducts = useMemo(
    () => (jsonProducts && jsonProducts.length ? jsonProducts : products),
    [jsonProducts, products]
  );

  const filteredProducts = useMemo(() => dataProducts, [dataProducts]);

  const categories = useMemo(
    () => {
      const uniqueCategories = [
        ...new Set(filteredProducts.map((p) => p.category?.name).filter(Boolean)),
      ];

      return ["all", ...sortCategories(uniqueCategories)];
    },
    [filteredProducts]
  );

  const [, startTransition] = useTransition();

  const handleSelect = useCallback((p) => {
    setSelectedProduct(p);
    setShowMenu(true);
  }, []);

  const groupedProducts = useMemo(() => {
    const q = norm(deferredSearch);
    const groups = {};

    for (const p of filteredProducts) {
      const catName = p.category?.name || "Разное";
      if (selectedCat !== "all" && catName !== selectedCat) continue;
      if (
        q &&
        !norm(p.name || "")
          .split(/\s+/)
          .some((w) => w.startsWith(q))
      )
        continue;

      if (!groups[catName]) groups[catName] = [];
      groups[catName].push(p);
    }

    const orderedCategories = sortCategories(Object.keys(groups));
    return Object.fromEntries(orderedCategories.map((catName) => [catName, groups[catName]]));
  }, [filteredProducts, selectedCat, deferredSearch]);

  useEffect(() => {
    const onDown = (e) => {
      if (catOpen && catRef.current && !catRef.current.contains(e.target)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [catOpen]);

  if (loading) {
    return (
      <div className="shop" style={{ padding: "20px", textAlign: "center" }}>
        <p>Загрузка меню...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shop" style={{ padding: "20px", textAlign: "center" }}>
        <p style={{ color: "red" }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="shop">
      <div className="shop__topbar">
        <div className={`shop__dropdown ${catOpen ? "open" : ""}`} ref={catRef}>
          <button
            type="button"
            className="shop__catBtn"
            aria-haspopup="listbox"
            aria-expanded={catOpen}
            onClick={() => setCatOpen((v) => !v)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Категории
          </button>

          <ul className="shop__dropList" role="listbox" tabIndex="-1">
            {categories.map((cat) => (
              <li key={cat}>
                <button
                  type="button"
                  className={"shop__dropItem" + (cat === selectedCat ? " selected" : "")}
                  role="option"
                  aria-selected={cat === selectedCat}
                  onClick={() =>
                    startTransition(() => {
                      setSelectedCat(cat);
                      setCatOpen(false);
                    })
                  }
                >
                  {cat === "all" ? "Все категории" : cat}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="shop__searchWrap" ref={searchBoxRef}>
          <svg className="shop__searchIcon" width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <line x1="16.65" y1="16.65" x2="22" y2="22" stroke="currentColor" strokeWidth="2" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="shop__searchInput"
            placeholder="Искать по названию"
          />
        </div>
      </div>

      {Object.entries(groupedProducts).map(([cat, items]) => (
        <div key={cat} className="shop__section">
          {(selectedCat === "all" || Object.keys(groupedProducts).length > 1) && (
            <div className="shop__catHeader">
              <h2 className="shop__catTitle">{cat}</h2>
              <span className="shop__catCount">&middot; {items.length}</span>
            </div>
          )}
          <div className="shop__list">
            {items.map((p) => (
              <ProductRow key={p._id} p={p} onSelect={handleSelect} />
            ))}
          </div>
        </div>
      ))}

      {createPortal(
        <div
          className="popup-portal shop"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2147483647,
            pointerEvents: showMenu ? "auto" : "none",
          }}
        >
          <PopupMenu
            set={setShowMenu}
            show={showMenu}
            title={ selectedProduct?.name || ""}
          >
            {selectedProduct && (
              <div className="shop-popup">
                <div className="shop-popup__img-wrap">
                  <img
                    src={selectedProduct.photo || soon}
                    alt={selectedProduct.name}
                    className="shop-popup__img"
                    onError={onImgError}
                    decoding="async"
                  />
                  {(selectedProduct.price != null || selectedProduct.volume) && (
                    <div className="shop-popup__badge-row">
                      {selectedProduct.price != null && (
                        <div className="shop-popup__price-badge">
                          {selectedProduct.price} ₽
                        </div>
                      )}
                      {selectedProduct.volume && (
                        <div className="shop-popup__volume-badge">
                          {selectedProduct.volume}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {(selectedProduct.subCategory || selectedProduct.composition || selectedProduct.country || selectedProduct.rehash != null) && (
                  <div className="shop-popup__card">
                    {selectedProduct.subCategory && (
                      <div className="shop-popup__row">
                        <span className="shop-popup__label">Подкатегория</span>
                        <span className="shop-popup__value">{selectedProduct.subCategory}</span>
                      </div>
                    )}
                    {selectedProduct.composition && (
                      <div className="shop-popup__row shop-popup__row--block">
                        <span className="shop-popup__label">Состав</span>
                        <span className="shop-popup__value"><Nl text={selectedProduct.composition} /></span>
                      </div>
                    )}
                    {selectedProduct.country && (
                      <div className="shop-popup__row">
                        <span className="shop-popup__label">Страна</span>
                        <span className="shop-popup__value">{selectedProduct.country}</span>
                      </div>
                    )}
                    {selectedProduct.rehash != null && (
                      <div className="shop-popup__row">
                        <span className="shop-popup__label">Перезабивка</span>
                        <span className="shop-popup__value">{selectedProduct.rehash} ₽</span>
                      </div>
                    )}
                  </div>
                )}

                {Array.isArray(selectedProduct.extras) &&
                  selectedProduct.extras.length > 0 &&
                  selectedProduct.extrasLabel !== "none" && (
                  <div className="shop-popup__card">
                    {selectedProduct.extrasLabel !== "empty" && (
                      <p className="shop-popup__section-label">
                        {selectedProduct.extrasLabel || "Дополнительно"}
                      </p>
                    )}
                    <div className="shop-popup__tags">
                      {selectedProduct.extras.map((tag, i) => (
                        <span key={i} className="shop-popup__tag">{String(tag)}</span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedProduct.desc && (
                  <p className="shop-popup__desc"><Nl text={selectedProduct.desc} /></p>
                )}
              </div>
            )}
          </PopupMenu>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Shop;